import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as api from '../lib/api';
import type { GlobalSearchResult, GlobalSearchSource, SelectedGlobalContext } from '../types';

const Wrap = styled.div`
  position: relative;
  flex: 1 1 min(560px, 46vw);
  max-width: 660px;
  min-width: 280px;

  @media (max-width: 760px) {
    width: 100%;
    max-width: none;
    min-width: 0;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  padding: 11px 14px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 12px;
  background: ${p => p.theme.surface};
  color: ${p => p.theme.text};
  outline: none;
  font-size: 14px;
  transition: background .15s ease, border-color .15s ease, box-shadow .15s ease;

  &::placeholder { color: ${p => p.theme.muted}; }

  &:focus {
    background: ${p => p.theme.surfaceStrong};
    border-color: ${p => p.theme.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.primarySoft};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 90;
  max-height: min(560px, calc(100vh - 96px));
  overflow-y: auto;
  border: 1px solid ${p => p.theme.border};
  border-radius: 14px;
  background: ${p => p.theme.surface};
  color: ${p => p.theme.text};
  box-shadow: 0 18px 54px rgba(0,0,0,0.32);
`;

const Status = styled.div`
  padding: 14px 16px;
  color: ${p => p.theme.muted};
  font-size: 13px;
`;

const Warning = styled(Status)`
  color: ${p => p.theme.warning};
  border-bottom: 1px solid ${p => p.theme.border};
`;

const GroupTitle = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 10px 14px 7px;
  background: ${p => p.theme.surface};
  color: ${p => p.theme.muted};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
`;

const ResultButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 0;
  border-top: 1px solid ${p => p.theme.border};
  background: ${p => (p.$active ? p.theme.primarySoft : 'transparent')};
  color: ${p => p.theme.text};
  text-align: left;

  &:hover { background: ${p => p.theme.primarySoft}; }
`;

const Main = styled.div`
  min-width: 0;
`;

const Title = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 800;
`;

const Subtitle = styled.div`
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.muted};
  font-size: 12px;
`;

const Tags = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 7px;
  background: ${p => p.theme.surfaceSoft};
  color: ${p => p.theme.muted};
  font-size: 11px;
  font-weight: 800;
`;

type Props = {
  onSelectContext: (context: SelectedGlobalContext) => void;
};

function sourceLabel(source: GlobalSearchSource) {
  if (source === 'metabase') return 'Workspace';
  return source === 'pipedrive' ? 'Pipedrive' : 'PostHog';
}

function contextFromResult(result: GlobalSearchResult): SelectedGlobalContext {
  if (result.type === 'company') {
    return { selectedType: 'company', selectedAt: Date.now(), company: result.company };
  }
  if (result.type === 'workspace') {
    return { selectedType: 'workspace', selectedAt: Date.now(), company: result.company, workspace: result.workspace };
  }
  return {
    selectedType: 'user',
    selectedAt: Date.now(),
    company: result.company || null,
    workspace: result.workspace || null,
    user: result.user,
  };
}

function resultTags(result: GlobalSearchResult) {
  if (result.type === 'company') return ['Company'];
  if (result.type === 'workspace') return ['Workspace'];
  const sources = result.user.sources || [];
  const external = sources.filter(source => source !== 'metabase');
  if (sources.includes('metabase') && external.length > 1) return ['Workspace', 'Both'];
  if (sources.includes('metabase')) return sources.map(sourceLabel);
  return external.length > 1 ? ['Both'] : external.map(sourceLabel);
}

function groupLabel(type: GlobalSearchResult['type']) {
  if (type === 'company') return 'Companies';
  if (type === 'workspace') return 'Workspaces';
  return 'Users';
}

export function GlobalSearch({ onSelectContext }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    const term = query.trim();
    const requestId = ++requestRef.current;
    setActiveIndex(0);

    if (term.length < 2) {
      setResults([]);
      setWarnings([]);
      setError('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    const timeout = window.setTimeout(() => {
      api.searchGlobal(term)
        .then(response => {
          if (requestRef.current !== requestId) return;
          setResults(response.results);
          setWarnings(response.warnings);
          setOpen(true);
        })
        .catch(apiError => {
          if (requestRef.current !== requestId) return;
          setResults([]);
          setWarnings([]);
          setError(apiError.response?.data?.error || apiError.message || 'Search failed');
          setOpen(true);
        })
        .finally(() => {
          if (requestRef.current === requestId) setIsLoading(false);
        });
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [query]);

  function selectResult(result: GlobalSearchResult) {
    onSelectContext(contextFromResult(result));
    setQuery('');
    setResults([]);
    setWarnings([]);
    setError('');
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open || !results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(index => Math.min(index + 1, results.length - 1));
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(index - 1, 0));
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      selectResult(results[activeIndex]);
    }
  }

  function renderGroup(type: GlobalSearchResult['type']) {
    const groupResults = results.filter(result => result.type === type);
    if (!groupResults.length) return null;
    return (
      <div key={type}>
        <GroupTitle>{groupLabel(type)}</GroupTitle>
        {groupResults.map(result => {
          const index = results.findIndex(item => item.id === result.id);
          return (
            <ResultButton
              key={result.id}
              type="button"
              $active={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={event => event.preventDefault()}
              onClick={() => selectResult(result)}
            >
              <Main>
                <Title title={result.label}>{result.label}</Title>
                {result.subtitle && <Subtitle title={result.subtitle}>{result.subtitle}</Subtitle>}
              </Main>
              <Tags>{resultTags(result).map(tag => <Tag key={tag}>{tag}</Tag>)}</Tags>
            </ResultButton>
          );
        })}
      </div>
    );
  }

  const shouldShowDropdown = open && (query.trim().length >= 2 || isLoading || error);

  return (
    <Wrap>
      <SearchInput
        value={query}
        onChange={event => { setQuery(event.target.value); setOpen(true); }}
        onFocus={() => { if (query.trim().length >= 2) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder="Search companies, workspaces, users"
        aria-label="Global search"
      />
      {shouldShowDropdown && (
        <Dropdown>
          {warnings.map(warning => <Warning key={warning}>{warning}</Warning>)}
          {error && <Status>{error}</Status>}
          {!error && isLoading && <Status>Searching...</Status>}
          {!error && !isLoading && !results.length && <Status>No results found.</Status>}
          {!error && results.length > 0 && ['company', 'workspace', 'user'].map(type => renderGroup(type as GlobalSearchResult['type']))}
        </Dropdown>
      )}
    </Wrap>
  );
}
